import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { CODES } from "../../consts/codes";
import { GetGeographiesList } from "../../services/admin/geographies/geographiesService";
import { GetCompaniesByCountry, ListBusinessGroup } from "../../services/user/businessGroup";

/** Slice de Redux para el estado de grupos empresariales. */
export const businessGroupSlice = createSlice({
  name: "businessGroup",
  initialState: {
    isBusinessGroup: false,
    lstCompanies: [],
    businessGroupStatus: "fetch",
    countriesAndCompanies: [],
    countriesAndCompaniesStatus: "fetch",
    error: null,
  },
  reducers: {
    /** Resetea el estado de countriesAndCompaniesStatus a "fetch". */
    resetCountriesAndCompaniesState(state, action) {
      state.countriesAndCompaniesStatus = "fetch";
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchCountriesAndCompanies.pending, (state, action) => {
        state.countriesAndCompaniesStatus = "loading";
      })
      .addCase(fetchCountriesAndCompanies.fulfilled, (state, action) => {
        state.countriesAndCompaniesStatus = "succeeded";
        state.countriesAndCompanies = action.payload;
      })
      .addCase(fetchCountriesAndCompanies.rejected, (state, action) => {
        state.countriesAndCompaniesStatus = "failed";
        state.countriesAndCompanies = [];
      })
      .addCase(fetchBusinessGroup.pending, (state, action) => {
        state.businessGroupStatus = "loading";
      })
      .addCase(fetchBusinessGroup.fulfilled, (state, action) => {
        const { isBusinessGroup, lstCompanies } = action.payload;

        state.businessGroupStatus = "succeeded";
        state.isBusinessGroup = isBusinessGroup;
        state.lstCompanies = lstCompanies;
      })
      .addCase(fetchBusinessGroup.rejected, (state, action) => {
        state.businessGroupStatus = "failed";
        state.isBusinessGroup = false;
        state.lstCompanies = [];
      });
  },
});

/** Devuelve el indicador de si el usuario pertenece a un grupo empresarial. */
export const getIsBusinessGroup = (state) => state.businessGroup.isBusinessGroup;

/** Devuelve la lista de empresas del grupo empresarial. */
export const getLstCompanies = (state) => state.businessGroup.lstCompanies;

/** Devuelve el estado de la petición de grupo empresarial. */
export const getIsBusinessGroupStatus = (state) => state.businessGroup.businessGroupStatus;

/** Devuelve la lista de países con sus empresas asociadas. */
export const getCountriesAndCompanies = (state) => state.businessGroup.countriesAndCompanies;

/** Devuelve el estado de la petición de países y empresas. */
export const getCountriesAndCompaniesStatus = (state) =>
  state.businessGroup.countriesAndCompaniesStatus;

export const { resetCountriesAndCompaniesState } = businessGroupSlice.actions;

/**
 * Obtiene el grupo empresarial y la lista de empresas asociadas a un país,
 * compañía EAN, email y tipo de opción.
 */
export const fetchBusinessGroup = createAsyncThunk(
  "businessGroup/fetchBusinessGroup",
  async ({ country, eanCompany, email, idOptionType }) => {
    const listService = await ListBusinessGroup({
      country: country,
      eanCompany: eanCompany,
      email: email,
      idOptionType: idOptionType,
    });

    const responseCode = Number(listService.data.responseCode);

    switch (responseCode) {
      case CODES.COD_RESPONSE_SUCCESS_REQUEST:
        return {
          isBusinessGroup: true,
          lstCompanies: listService.data.data,
        };

      case CODES.COD_RESPONSE_ERROR_LOGIN:
        return { isBusinessGroup: false, lstCompanies: [] };

      default:
        return { isBusinessGroup: false, lstCompanies: [] };
    }
  },
);

/**
 * Obtiene la lista de países con sus empresas asociadas.
 */
export const fetchCountriesAndCompanies = createAsyncThunk(
  "businessGroup/fetchCountriesAndCompanies",
  async () => {
    const requestCountries = await GetGeographiesList();

    const responseCodeCountries = requestCountries.data.responseCode;

    switch (responseCodeCountries) {
      case CODES.COD_RESPONSE_SUCCESS:
        const countries = requestCountries.data.responseMessage;

        let countriesAndCompanies = [];

        for (const country of countries) {
          let lstCompanies = [];

          const requestCompaniesByCountry = await GetCompaniesByCountry({
            country: country.country,
            lstEansProviders: null,
          });

          const responseCodeCompany = requestCompaniesByCountry.data.responseCode;

          if (responseCodeCompany === CODES.COD_RESPONSE_SUCCESS_REQUEST) {
            let companies = requestCompaniesByCountry.data.responseMessage;
            lstCompanies = companies;
          }

          countriesAndCompanies.push({
            ...country,
            lstCompanies: lstCompanies,
          });
        }

        return countriesAndCompanies;

      case CODES.COD_RESPONSE_ERROR_LOGIN:
        return [];

      default:
        return [];
    }
  },
);

/** Reducer del slice de grupos empresariales. */
export default businessGroupSlice.reducer;